import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/core/AuthContext';
import { useUserTenants } from '@/core/hooks/useTenant';
import { createTenantMembership } from '@/core/api/tenants';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Badge } from '@/shared/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { useToast } from '@/shared/hooks/use-toast';
import { Building2, Search, Clock, CheckCircle, XCircle, Plus } from 'lucide-react';

interface TenantData {
  id: string;
  name: string;
  code: string;
}

const JoinFactory = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: memberships = [] } = useUserTenants();

  const { data: tenants = [], isLoading } = useQuery({
    queryKey: ['available-tenants', searchQuery],
    queryFn: async () => {
      let query = supabase
        .schema('core')
        .from('tenants')
        .select('id, name, code')
        .eq('is_active', true)
        .order('name');

      if (searchQuery) {
        query = query.or(`name.ilike.%${searchQuery}%,code.ilike.%${searchQuery}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as TenantData[];
    }
  });

  const joinMutation = useMutation({
    mutationFn: async (tenantId: string) => {
      if (!user) throw new Error('Not authenticated');
      await createTenantMembership(tenantId, user.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-memberships'] });
      toast({
        title: 'Request sent!',
        description: 'A tenant supervisor will review your request.'
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to send request',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  const getTenantStatus = (tenantId: string) => {
    const membership = memberships.find((member) => member.tenant_id === tenantId);
    return membership?.status;
  };

  const getStatusBadge = (status: string | undefined) => {
    switch (status) {
      case 'approved':
        return <Badge variant="default" className="bg-success"><CheckCircle className="h-3 w-3 mr-1" />Member</Badge>;
      case 'pending':
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      default:
        return null;
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Building2 className="h-6 w-6 text-primary" />
          Join a Tenant
        </h1>
        <p className="text-muted-foreground">Request access to a tenant organization</p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search tenants by name or code..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading tenants...</div>
      ) : tenants.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No tenants found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {tenants.map((tenant) => {
            const status = getTenantStatus(tenant.id);

            return (
              <Card key={tenant.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Building2 className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{tenant.name}</CardTitle>
                        <Badge variant="secondary" className="mt-1">{tenant.code}</Badge>
                      </div>
                    </div>
                    {getStatusBadge(status)}
                  </div>
                </CardHeader>
                <CardContent>
                  {!status && (
                    <Button
                      variant="industrial"
                      className="w-full"
                      onClick={() => joinMutation.mutate(tenant.id)}
                      disabled={joinMutation.isPending}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Request Access
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default JoinFactory;
